/**
 * Интеграция с ЭЦП через NCALayer (WebSocket wss://127.0.0.1:13579).
 * Требует установленное и запущенное приложение NCALayer: https://ncl.pki.gov.kz/
 */
import { NCALayerClient } from 'ncalayer-js-client';

export interface Certificate {
  alias: string;
  thumbprint: string;
  subject: string;
  issuer: string;
  validFrom: string;
  validTo: string;
}

export interface SignatureResult {
  success: boolean;
  signedPdf?: string;
  signature?: string;
  certificate?: {
    thumbprint: string;
    subject: string;
    issuer: string;
  };
  signedAt?: string;
  error?: string;
  canceledByUser?: boolean;
}

export interface BatchSignatureResult {
  success: boolean;
  signatures: string[];
  error?: string;
  canceledByUser?: boolean;
  method?: 'ncalayer-single' | 'ncalayer-multisign';
}

function isCanceledByUser(error: unknown): boolean {
  return Boolean(
    error &&
      typeof error === 'object' &&
      'canceledByUser' in error &&
      (error as { canceledByUser?: boolean }).canceledByUser
  );
}

class NCLayerService {
  private client: NCALayerClient | null = null;
  private initialized = false;
  private version: string | null = null;

  private isSocketOpen(): boolean {
    return this.client?.wsConnection?.readyState === WebSocket.OPEN;
  }

  /**
   * Подключается к локальному NCALayer.
   */
  async initialize(): Promise<boolean> {
    try {
      await this.ensureConnected();
      return true;
    } catch (error) {
      console.error('[NCALayer] Error initializing:', error);
      return false;
    }
  }

  /**
   * Гарантирует активное WebSocket-подключение к NCALayer.
   */
  async ensureConnected(): Promise<string> {
    if (this.isSocketOpen() && this.client) {
      return this.version || 'connected';
    }

    this.client = new NCALayerClient(undefined, false);
    // true — быстрый HTTP-probe, не зависает на минуты если NCALayer не запущен
    this.version = await this.client.connect(true);
    this.initialized = true;
    console.log('[NCALayer] Connected, version:', this.version);
    return this.version;
  }

  isAvailable(): boolean {
    return this.initialized && this.isSocketOpen();
  }

  /**
   * Возвращает типы активных хранилищ (PKCS12, KAZTOKEN и т.д.).
   * Выбор сертификата выполняется в UI NCALayer при подписании.
   */
  async listCertificates(): Promise<Certificate[]> {
    try {
      await this.ensureConnected();
      if (!this.client) {
        return [];
      }

      const tokens = await this.client.getActiveTokens();
      console.log('[NCALayer] Active tokens:', tokens);

      return (tokens.length > 0 ? tokens : [NCALayerClient.fileStorageType]).map((token) => ({
        alias: token,
        thumbprint: token,
        subject: `Хранилище: ${token}`,
        issuer: 'NCALayer',
        validFrom: '',
        validTo: '',
      }));
    } catch (error) {
      console.error('[NCALayer] Error listing certificates:', error);
      throw error;
    }
  }

  /**
   * Подписывает dataToSign (ByteRange для PAdES) detached CMS через NCALayer.
   */
  async signDocument(dataToSignBase64: string, certificateAlias?: string): Promise<SignatureResult> {
    return this.signCms(dataToSignBase64, 'detached', certificateAlias);
  }

  /**
   * Attached CMS: документ вложен в подпись (формат ezSigner.kz).
   */
  async signAttached(documentBase64: string, certificateAlias?: string): Promise<SignatureResult> {
    return this.signCms(documentBase64, 'attached', certificateAlias);
  }

  /**
   * Пакетная attached CMS: массив PDF в одном basicsSignCMS (одно окно NCALayer).
   */
  async signAttachedBatch(
    documentsBase64: string[],
    onProgress?: (current: number, total: number) => void
  ): Promise<BatchSignatureResult> {
    if (!documentsBase64.length) {
      return { success: false, signatures: [], error: 'Нет документов для подписи' };
    }

    if (documentsBase64.length === 1) {
      const single = await this.signAttached(documentsBase64[0]);
      return {
        success: single.success,
        signatures: single.success && single.signature ? [single.signature] : [],
        canceledByUser: single.canceledByUser,
        error: single.error,
        method: 'ncalayer-single',
      };
    }

    try {
      await this.ensureConnected();
      if (!this.client) {
        throw new Error('NCALayer не подключена');
      }

      onProgress?.(0, documentsBase64.length);
      console.log('[NCALayer] Multisign via basicsSignCMS array, docs:', documentsBase64.length);
      const signature = await this.client.basicsSignCMS(
        NCALayerClient.basicsStorageAll,
        documentsBase64,
        NCALayerClient.basicsCMSParamsAttached,
        NCALayerClient.basicsSignerSignAny,
        'ru'
      );
      const signatures = Array.isArray(signature) ? signature : [signature];
      onProgress?.(documentsBase64.length, documentsBase64.length);

      if (signatures.length !== documentsBase64.length) {
        return {
          success: false,
          signatures,
          error: 'Неполный результат мультиподписи NCALayer',
          method: 'ncalayer-multisign',
        };
      }

      return { success: true, signatures, method: 'ncalayer-multisign' };
    } catch (error) {
      if (isCanceledByUser(error)) {
        return {
          success: false,
          signatures: [],
          canceledByUser: true,
          error: 'Операция отменена пользователем',
          method: 'ncalayer-multisign',
        };
      }
      console.warn(
        '[NCALayer] Multisign failed, fallback to single:',
        error instanceof Error ? error.message : error
      );
    }

    const signatures: string[] = [];
    for (let i = 0; i < documentsBase64.length; i += 1) {
      onProgress?.(i + 1, documentsBase64.length);
      const result = await this.signAttached(documentsBase64[i]);
      if (result.canceledByUser) {
        return {
          success: false,
          signatures,
          canceledByUser: true,
          error: 'Операция отменена пользователем',
          method: 'ncalayer-single',
        };
      }
      if (!result.success || !result.signature) {
        return {
          success: false,
          signatures,
          error: result.error || 'Ошибка подписи',
          method: 'ncalayer-single',
        };
      }
      signatures.push(result.signature);
    }

    return { success: true, signatures, method: 'ncalayer-single' };
  }

  private async signCms(
    dataBase64: string,
    mode: 'detached' | 'attached',
    certificateAlias?: string
  ): Promise<SignatureResult> {
    try {
      if (!dataBase64) {
        throw new Error('Пустые данные для подписи');
      }

      await this.ensureConnected();
      if (!this.client) {
        throw new Error('NCALayer не подключена');
      }

      const params =
        mode === 'attached'
          ? NCALayerClient.basicsCMSParamsAttached
          : NCALayerClient.basicsCMSParamsDetached;

      console.log(`[NCALayer] Requesting ${mode} CMS via basicsSignCMS...`);

      const signature = await this.client.basicsSignCMS(
        NCALayerClient.basicsStorageAll,
        dataBase64,
        params,
        NCALayerClient.basicsSignerSignAny,
        'ru'
      );

      const cms = Array.isArray(signature) ? signature[0] : signature;
      console.log(`[NCALayer] ${mode} CMS created successfully`);

      return {
        success: true,
        signature: cms,
        certificate: {
          thumbprint: certificateAlias || 'ncalayer-cms',
          subject: '',
          issuer: '',
        },
        signedAt: new Date().toISOString(),
      };
    } catch (error) {
      console.error(`[NCALayer] Error signing (${mode}):`, error);

      return {
        success: false,
        canceledByUser: isCanceledByUser(error),
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  showRequirements(): void {
    alert(
      'Для подписи через NCALayer необходимо:\n' +
        '• Windows\n' +
        '• Браузер Chrome или Edge\n' +
        '• Установленное и запущенное приложение NCALayer\n' +
        '• Казахстанский сертификат ЭЦП\n\n' +
        'Скачать NCALayer:\nhttps://ncl.pki.gov.kz/'
    );
  }

  async logDebugInfo(): Promise<void> {
    try {
      console.log('[NCALayer Debug Info]', {
        platform: navigator.platform,
        isAvailable: this.isAvailable(),
        version: this.version,
        userAgent: navigator.userAgent,
      });
    } catch (error) {
      console.error('[NCALayer] Error logging debug info:', error);
    }
  }
}

export const nclayerService = new NCLayerService();
